import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { auth } from "./firebase";
import { 
  FiArrowLeft, 
  FiDownload, 
  FiLoader, 
  FiCheckCircle,
  FiAlertTriangle
} from "react-icons/fi";
const CONFIG = require("./config");

function normalizeSkillList(skills) {
  if (Array.isArray(skills)) {
    return skills
      .map((item) => String(item || "").trim())
      .filter(Boolean)
      .slice(0, 8);
  }

  if (typeof skills === "string") {
    const trimmed = skills.trim();
    if (!trimmed) {
      return [];
    }

    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed
          .map((item) => String(item || "").trim())
          .filter(Boolean)
          .slice(0, 8);
      }
    } catch (_error) {
      return trimmed
        .split(/[,;\n]+/)
        .map((item) => item.trim())
        .filter(Boolean)
        .slice(0, 8);
    }
  }

  return [];
}

function toSingleSentence(text) {
  const normalized = String(text || "").replace(/\s+/g, " ").trim();
  if (!normalized) {
    return "No summary available.";
  }

  const noEllipsis = normalized.replace(/\.{3,}\s*$/, "").trim();
  const sentence = noEllipsis.split(/(?<=[.!?])\s+/)[0] || noEllipsis;
  if (/[.!?]$/.test(sentence)) {
    return sentence;
  }
  return `${sentence}.`;
}

function normalizeCandidateName(value) {
  if (value === null || value === undefined) {
    return "";
  }

  const text = String(value).trim().replace(/\s+/g, " ");
  if (!text) {
    return "";
  }

  const normalized = text.toLowerCase().replace(/[\s\-_]+/g, " ").trim();
  const compact = normalized.replace(/[^a-z0-9]+/g, "");
  const unknownValues = new Set([
    "unknown",
    "none",
    "non",
    "n/a",
    "na",
    "null",
    "unknown candidate",
    "not sure",
    "unsure",
    "uncertain",
    "cannot determine",
    "cant determine",
    "not provided",
    "no name",
    "no candidate name",
  ]);
  const unknownCompact = new Set([
    "unknown",
    "none",
    "non",
    "na",
    "null",
    "unknowncandidate",
    "notsure",
    "unsure",
    "uncertain",
    "cannotdetermine",
    "cantdetermine",
    "notprovided",
    "noname",
    "nocandidatename",
  ]);

  if (unknownValues.has(normalized) || unknownCompact.has(compact)) {
    return "";
  }

  return text;
}

function ResumeDetail() {
  const { resumeId } = useParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  const [resumeData, setResumeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toastMessage, setToastMessage] = useState("");
  const [showToast, setShowToast] = useState(false);
  
  // Memoize fetchResumeDetails to prevent dependency issues
  const fetchResumeDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = await auth.currentUser.getIdToken();
      const response = await fetch(`${CONFIG.API_URL}/resume/${resumeId}`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (!response.ok) {
        throw new Error("Failed to fetch resume details");
      }
      
      const data = await response.json();
      setResumeData(data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching resume details:", error);
      setError("Failed to load resume details. Please try again.");
      setLoading(false);
    }
  }, [resumeId]);
  
  // Fetch resume details on component mount
  useEffect(() => {
    if (currentUser && resumeId) {
      fetchResumeDetails();
    }
  }, [currentUser, resumeId, fetchResumeDetails]);
  
  const resumeFileName = resumeData?.original_filename || resumeData?.filename || "Resume";
  const candidateName = normalizeCandidateName(resumeData?.candidate_name);
  const resumeName = (candidateName || resumeFileName).replace(/\.pdf$/i, "");
  const matchedSkills = normalizeSkillList(resumeData?.matched_skills);
  const missingSkills = normalizeSkillList(resumeData?.missing_skills);
  const safeSummary = toSingleSentence(resumeData?.match_summary);

  // Download resume file
  const downloadResume = async () => {
    try {
      const token = await auth.currentUser.getIdToken();
      const response = await fetch(`${CONFIG.API_URL}/file/${resumeData.id}.pdf`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (!response.ok) {
        throw new Error("Failed to download resume");
      }
      
      // Create a blob from the response
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      // Create a temporary link and trigger the download
      const a = document.createElement("a");
      a.href = url;
      a.download = resumeFileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      
      // Show success message
      showToastMessage("Resume downloaded successfully");
    } catch (error) {
      console.error("Error downloading resume:", error);
      showToastMessage("Error downloading resume");
    }
  };
  
  // Navigate back to job details
  const goBack = () => {
    navigate(`/dashboard/overview`);
  };
  
  // Show toast message
  const showToastMessage = (message) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => {
      setShowToast(false);
    }, 3000);
  };
  
  // If user is not logged in, show login prompt
  if (!currentUser) {
    return (
      <div className="login-prompt">
        <h2>Please log in to access your dashboard</h2>
        <p>You need to be logged in to view resume details.</p>
        <a href="/app/login" className="primary-button">Log In</a>
      </div>
    );
  }
  
  // Show loading state
  if (loading) {
    return (
      <div className="resume-detail-container">
        <div className="loading-spinner">
          <FiLoader className="spinner" />
          <p>Loading resume details...</p>
        </div>
      </div>
    );
  }
  
  // Show error state
  if (error) {
    return (
      <div className="resume-detail-container">
        <div className="error-message">
          <FiAlertTriangle className="error-icon" />
          <h3>Error Loading Resume</h3>
          <p>{error}</p>
          <button className="primary-button" onClick={goBack}>
            Back to Jobs
          </button>
        </div>
      </div>
    );
  }
  
  // Show resume details
  return (
    <div className="resume-detail-container">
      {/* Header with back button and actions */}
      <div className="resume-detail-header">
        <button className="back-button" onClick={goBack}>
          <FiArrowLeft /> Back to Job
        </button>
        
        <div className="resume-detail-title">
          <h2>{resumeName}</h2>
          <p>From job: {resumeData.job_title}</p>
          <p className="resume-detail-file">File name: {resumeFileName}</p>
        </div>
        
        <div className="resume-detail-actions">
          <button className="primary-button" onClick={downloadResume}>
            <FiDownload /> Download Resume
          </button>
        </div>
      </div>
      
      {/* Match information */}
      <div className="match-info-container">
        <div className="match-percentage-card">
          <div className="match-percentage-header">Match Score</div>
          <div 
            className="match-percentage-value"
            style={{ 
              color: resumeData.match_percentage > 80 ? 'var(--success-color)' : 
                     resumeData.match_percentage > 60 ? 'var(--primary-color)' : 
                     resumeData.match_percentage > 40 ? 'var(--warning-color)' : 
                     'var(--danger-color)'
            }}
          >
            {resumeData.match_percentage}%
          </div>
          <div className="match-percentage-bar-container">
            <div 
              className="match-percentage-bar" 
              style={{ 
                width: `${resumeData.match_percentage}%`,
                backgroundColor: resumeData.match_percentage > 80 ? 'var(--success-color)' : 
                                resumeData.match_percentage > 60 ? 'var(--primary-color)' : 
                                resumeData.match_percentage > 40 ? 'var(--warning-color)' : 
                                'var(--danger-color)'
              }}
            ></div>
          </div>
        </div>
        
        <div className="match-summary-card">
          <div className="match-summary-header">Match Summary</div>
          <div className="match-summary-content">
            {safeSummary}
          </div>
        </div>

        <div className="match-summary-card match-skills-card">
          <div className="match-summary-header">Skills Breakdown</div>
          <div className="detail-skill-group">
            <h4>Matched Skills</h4>
            <div className="reason-chips detail-reason-chips">
              {matchedSkills.length > 0 ? (
                matchedSkills.map((skill) => (
                  <span key={`matched-${skill}`} className="reason-chip matched">
                    {skill}
                  </span>
                ))
              ) : (
                <span className="empty-skills-message">No matched skills available yet.</span>
              )}
            </div>
          </div>
          <div className="detail-skill-group">
            <h4>Missing Skills</h4>
            <div className="reason-chips detail-reason-chips">
              {missingSkills.length > 0 ? (
                missingSkills.map((skill) => (
                  <span key={`missing-${skill}`} className="reason-chip missing">
                    {skill}
                  </span>
                ))
              ) : (
                <span className="empty-skills-message">No major gaps identified.</span>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Resume content */}
      <div className="resume-content-container">
        <h3>Resume Content</h3>
        <div className="resume-content">
          {resumeData.content ? (
            <pre>{resumeData.content}</pre>
          ) : (
            <p className="no-content-message">No content could be extracted from this resume.</p>
          )}
        </div>
      </div>
      
      {/* Toast message */}
      {showToast && (
        <div className="toast-message">
          <FiCheckCircle />
          <span>{toastMessage}</span>
        </div>
      )}
    </div>
  );
}

export default ResumeDetail;
